import { Check, Close } from "@mui/icons-material";
import { Autocomplete, Box, Button, FormHelperText, TextField, Typography } from "@mui/material";
import Modal from "../../../App/Components/Modal/Modal";
import { AppFeatureLookup } from "../../../App/Constants/AbacConstants";
import { useFeaturePermission } from "../../../App/State/Hooks/abacHooks";
import { useNotificationStore } from "../../../App/State/Stores/NotificationStore";
import { useGroupCandidateSelectionContext } from "../../State/GroupCandidateSelectionContext";
import { useMoveToGroupState } from "./State/MoveToGroupState";

interface IMoveToGroupProps {
  groupId: string;
  isModalOpen: boolean;
  setModalOpen: (isOpen: boolean) => void;
}

export function MoveToGroup({ groupId, isModalOpen, setModalOpen }: IMoveToGroupProps) {
  const {
    getters: { selectedCandidates: candidatesToAddToGroup, selectedCount },
    setters: { setCandidateSelectedList: setCandidatesToAddToGroup },
  } = useGroupCandidateSelectionContext();
  const addToGroupButtonAccess = useFeaturePermission(AppFeatureLookup.CandidatesAddCandidatesToGroup);
  const state = useMoveToGroupState(groupId, candidatesToAddToGroup, setModalOpen, setCandidatesToAddToGroup);
  const {
    getters: {
      selectedGroup,
      autoCompleteFilter,
      groupDropdownList = [],
      isLoadingMoveMembers,
      isLoadingSaveGroup,
    },
    handlers: { handleGroupSelect, handleGroupSave, handleCancelMoveToGroup },
  } = state;
  const snackbarSeverity = useNotificationStore((state) => state.type);

  const selectedText = `${selectedCount} candidate${selectedCount > 1 ? "s" : ""} selected`;
  const buttonText = `Move Candidate${selectedCount > 1 ? "s" : ""}`;

  return (
    <Modal
      title="Move Candidates to Group"
      dataTestId="move-candidates"
      isOpen={addToGroupButtonAccess && isModalOpen}
      content={
        <>
          <Box>
            <Autocomplete
              data-test="movetogroup-autocomplete"
              options={groupDropdownList}
              selectOnFocus
              clearOnBlur
              onChange={handleGroupSelect}
              filterOptions={(options, params) => {
                const filtered = autoCompleteFilter(options, params);

                const { inputValue } = params;

                // Suggest the creation of a new value
                const isExisting = options.some((option) => inputValue === option.name);
                if (inputValue !== "" && !isExisting) {
                  filtered.push({
                    id: "",
                    name: `Add "${inputValue}"`,
                  });
                }

                return filtered;
              }}
              handleHomeEndKeys
              getOptionLabel={(group) => {
                if (typeof group === "string") {
                  return group;
                } else {
                  return group.name;
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select a Group"
                  error={snackbarSeverity === "error" || snackbarSeverity === "warning"}
                  color={
                    snackbarSeverity === "error"
                      ? "error"
                      : snackbarSeverity === "warning"
                        ? "warning"
                        : "primary"
                  }
                  data-test="addtogroup-searchfield"
                />
              )}
              sx={{ width: 1 }}
            />
            <FormHelperText>Select candidates from the list using the checkboxes</FormHelperText>
            <FormHelperText>
              The candidates will be removed from this group and added to the selected group
            </FormHelperText>
          </Box>
          <Box sx={{ my: 1 }}>
            <Typography data-test="selected-count">{selectedText}</Typography>
          </Box>
        </>
      }
      footerContent={
        <Box sx={{ display: "flex", gap: "2ch", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            data-test={`movetogroup-save`}
            onClick={handleGroupSave}
            size="small"
            color="primary"
            aria-label="Submit candidates to group"
            title="Submit candidates to group"
            startIcon={<Check fontSize="small" />}
            disabled={!selectedGroup.name || isLoadingMoveMembers || isLoadingSaveGroup}
          >
            {buttonText}
          </Button>
          <Button
            variant="outlined"
            data-test={`movetogroup-cancel`}
            onClick={handleCancelMoveToGroup}
            size="small"
            color="warning"
            aria-label="Cancel adding candidates to group"
            title="Cancel adding candidates to group"
            startIcon={<Close fontSize="small" />}
          >
            Cancel
          </Button>
        </Box>
      }
      handleCloseModal={() => setModalOpen(false)}
    />
  );
}
